import {
  observable,
  makeObservable,
  action,
  computed,
  flow,
  reaction,
  autorun,
  when,
} from "mobx";
import {
  CHAT_ON_VIDEO_EDIT,
  CHAT_ON_VIDEO_COM,
  CHAT_ON_R1,
  CLIENT_CHAN,
} from "logic/src/objects/chat/constants";
import moment from "moment";
import { UploadedFile } from "logic/src/objects/file/types";
import {
  ESessionFileContentParseStatus,
  ESessionFileStatus,
  ESessionFileType,
  ISessionFile,
} from "../../objects/session/type";
import SessionMananger from "../../objects/session/manager";
import SessionFile from "../../objects/session/sessionFile";
import ChatSampleManager from "../../objects/chatSample/manager";
import Session from "../../objects/session/model";
import Chat from "../../objects/chat/model";
import { isImageFileName } from "../../objects/file/utils";
import {
  ESendMsgType,
  EChatType,
  EStreamDataStatus,
  ISelection,
  EFileQueryType,
  IChatSendMessage,
  EChatStatus,
  IChatTransLang,
  EStreamDataType,
  EChatModel,
  IWebSearch,
} from "../../objects/chat/types";
import { IChatSample } from "../../objects/chatSample/types";
import {
  DEFAULT_CONTENT_LANG,
  DEFAULT_TARGET_LANG,
  EChatErrorCode,
  FILE_QUERY_MSG,
} from "../../objects/chat/constants";
import user from "../../objects/user/model";
import GuessModel from "../../objects/guess/model";
import Video from "../../objects/video/model";
import { EVideoUploadStatus } from "logic/src/objects/video/types";

const LANG_CACHE_KEY = "translate_langs";

class VM {
  user = user;
  sessionMap: { [pro: string]: Session } = {};
  guess = new GuessModel();
  @observable sessionChanging: boolean = false;
  @observable video: Video = new Video();

  @observable sessionManager: SessionMananger | null;
  @observable session: Session;
  @observable.ref chatSampleManager: ChatSampleManager | null;
  @observable chat: Chat | null;
  @observable chatModel: EChatModel = EChatModel.Normal;
  @observable previewingFile: ISessionFile | null = null;
  @observable.ref fileSelectText: ISelection | null = null;

  @observable.ref web_search: boolean = false;

  @observable.ref showFileSelect: ISelection | null = null;

  @observable.ref chatContentLang: IChatTransLang = DEFAULT_CONTENT_LANG;
  @observable.ref chatTargetLang: IChatTransLang = DEFAULT_TARGET_LANG;

  @observable.ref currentWebSearch: IWebSearch[] = [];

  public constructor(chatModel?: EChatModel) {
    makeObservable(this);
    this.sessionManager = null;
    this.session = new Session("");
    this.chatSampleManager = null;
    this.chat = null;
    this.previewingFile = null;
    this.initChatLang();
    this.init = this.init.bind(this);
    this.selectSession = this.selectSession.bind(this);
    this.newSession = this.newSession.bind(this);
    this.editSessionName = this.editSessionName.bind(this);
    if (chatModel) {
      this.chatModel = chatModel;
    }

    // autorun(() => {
    //   const chatLen = this.session.chatInstanceList.length;
    //   if (chatLen === 1) {
    //     const firstChat = this.session.chatInstanceList[0];
    //     if (
    //       !firstChat.isRegen &&
    //       firstChat.type === EChatType.stream &&
    //       firstChat.streamingStatus === EStreamDataStatus.streaming
    //       // firstChat.streamingStatus === EStreamDataStatus.streamingEnd
    //     ) {
    //       this.handleFirstChat(firstChat.initialSessionTitle);
    //     }
    //   }
    // });

    // autorun(() => {
    //   const chatLen = this.session.chatInstanceList.length;
    //   if (chatLen) {
    //     const lastChat = this.session.chatInstanceList[chatLen - 1];
    //     if (
    //       lastChat.type === EChatType.stream &&
    //       lastChat.streamingStatus === EStreamDataStatus.streamingEnd
    //     ) {
    //       this.sessionManager!.pinnedInGroup(this.session.session_id);
    //       this.session.getSessionInfo();
    //     }
    //   }
    // });

    reaction(
      (): string | null => {
        const chatLen = this.session.chatInstanceList.length;
        if (
          this.session.is_deleted ||
          (chatLen &&
            this.session.chatInstanceList[chatLen - 1].errCode ===
              EChatErrorCode.sessionDeleted)
        ) {
          return this.session.session_id;
        }
        return null;
      },
      (curSessionId: string | null) => {
        if (curSessionId) {
          this.newSession();
          this.sessionManager?.deleteListSession(curSessionId);
          this.sessionManager?.deleteQuerySession(curSessionId);
        }
      },
    );

    autorun(() => {
      VM.cacheTranslationLang({
        contentLang: this.chatContentLang,
        targetLang: this.chatTargetLang,
      });
    });

    // 退出登录, 清空localstroage 缓存语言, 重置内容语言/目标语言
    reaction(
      () => !this.isLogin,
      () => {
        VM.cacheTranslationLang(null);
        this.initChatLang();
      },
    );
  }

  @computed
  get isLogin() {
    return this.user.isLogin;
  }

  @computed
  get highLightSelectionList() {
    if (!this.previewingFile) {
      return [];
    }

    const filelist = this.session.keyPointMap[this.previewingFile!.file_id!];
    return filelist
      ?.map(item => {
        try {
          const res = JSON.parse(item.file_query!.location);
          return {
            ...res,
            msgId: item.msgId,
            text: item.file_query?.file_query_msg,
          };
        } catch (e) {
          console.error(e);
        }
      })
      ?.filter(i => i);
  }

  @computed
  get canFetchHistory() {
    return (
      this.isLogin &&
      this.session.chatInstanceList.length &&
      !this.chat &&
      !this.hasAllHistory
    );
  }

  @computed
  get hasAllHistory() {
    return (
      this.session.chatInstanceList.length &&
      this.session.chatInstanceList[0].isRoot
    );
  }

  setPreviewFile(file: ISessionFile | null) {
    this.previewingFile = file;
  }

  @action.bound
  setChatModel(v: EChatModel) {
    this.chatModel = v;
  }

  @action.bound
  setCurrentWebSearch(v: IWebSearch[]) {
    this.currentWebSearch = v;
  }

  @action.bound
  setWebSearch(v: boolean) {
    this.web_search = v;
  }

  @action.bound
  setContentLang(v: IChatTransLang) {
    this.chatContentLang = v;
  }

  @action.bound
  setTargetLang(v: IChatTransLang) {
    this.chatTargetLang = v;
  }

  @action.bound
  initChatLang() {
    const { contentLang, targetLang } = VM.getDefaultTranslationLang();
    this.setContentLang(contentLang);
    this.setTargetLang(targetLang);
  }

  @action
  setFileSelectText(v: ISelection | null) {
    this.fileSelectText = v;
  }

  @action
  setShowFileSelect = (v: ISelection | null) => {
    this.showFileSelect = v;
  };

  @computed
  get isPending(): boolean {
    const res = this.session.chatInstanceList.some(
      (item: Chat) =>
        item.streamingStatus === EStreamDataStatus.init ||
        item.streamingStatus === EStreamDataStatus.streaming,
    );
    return res;
  }

  @action.bound
  selectPreviewFile(file_id: string) {
    const file = this.session.fileManager?.getFileById(file_id);
    if (!file || file.status === "success") {
      if (this.currentWebSearch.length) {
        this.setCurrentWebSearch([]);
      }
      this.setPreviewFile(file || null);
    }
  }

  @action.bound
  closePreviewFile() {
    this.setShowFileSelect(null);
    this.setPreviewFile(null);
  }

  public async init(presetMsg?: { sendMsg: string; recvMsg: string }) {
    this.setShowFileSelect(null);
    this.guess = new GuessModel();
    // 始终创建新的 Video 实例，确保 video 对象存在
    this.video = new Video();
    this.sessionManager = new SessionMananger();
    this.sessionManager.setClientChan(
      this.chatModel === EChatModel.Video
        ? CHAT_ON_VIDEO_EDIT + ";" + CHAT_ON_VIDEO_COM
        : CHAT_ON_R1 + ";" + CLIENT_CHAN,
    );
    this.chat = null;
    this.initChatLang();
    const isVideo =
      this.chatModel === EChatModel.VideoEdit ||
      this.chatModel === EChatModel.VideoR1 ||
      this.chatModel === EChatModel.Video;
    if (this.isLogin) {
      await this.sessionManager.listMember();
      if (presetMsg) {
        await this.handlePresetChat(presetMsg);
      } else {
        const { session_id } =
          VM.getCachedSessionInfo(isVideo ? "video" : undefined) || {};
        if (session_id) {
          this.session = new Session(session_id);
          if (isVideo) {
            await this.session.getFirstMessage();
            // 初始化视频
            this.initVideoFile();
            this.setChatModel(EChatModel.VideoEdit);
          }
          await this.session.getSessionFullInfo(isVideo);

          // 获取猜你想问
          if (
            this.session.chatInstanceList &&
            this.session.chatInstanceList.length
          ) {
            this.getGuessList(
              this.session.chatInstanceList[
                this.session.chatInstanceList.length - 1
              ],
            );
          }
        } else {
          await this.resetChatSample();
        }
      }
    } else {
      presetMsg
        ? this.handlePresetChat(presetMsg)
        : await this.resetChatSample();
    }

    console.log("--debug---init----", this.chatModel);
  }

  @action.bound
  async handlePresetChat(presetMsg?: { sendMsg: string; recvMsg: string }) {
    if (!presetMsg) return;
    if (this.isLogin) {
      this.session = new Session("");
      const isSuccess = await this.session.createQAChat(
        presetMsg.sendMsg,
        presetMsg.recvMsg,
      );
      // console.log("guess--isSuccess", isSuccess);
      // // 获取猜你想问
      // if (
      //   this.session.chatInstanceList &&
      //   this.session.chatInstanceList.length
      // ) {
      //   this.getGuessList(
      //     this.session.chatInstanceList[
      //       this.session.chatInstanceList.length - 1
      //     ],
      //   );
      // }

      if (isSuccess) {
        this.handleFirstChat(
          this.session.chatInstanceList[0]?.initialSessionTitle,
        );

        VM.saveSessionInfo2Storage(
          this.session.session_id,
          this.chatModel === EChatModel.Video ||
            this.chatModel === EChatModel.VideoEdit ||
            this.chatModel === EChatModel.VideoR1
            ? "video"
            : undefined,
        );
      } else {
        await this.resetChatSample();
      }
    } else {
      this.chatSampleManager = null;
      this.chat = new Chat({
        type: EChatType.history,
        streamingStatus: EStreamDataStatus.streamingEnd,
        send_msg: [
          {
            msg_type: ESendMsgType.user_query,
            user_query: presetMsg.sendMsg,
          },
        ],
        recv_msg: [
          {
            text: presetMsg.recvMsg,
            text2image: "",
            type: EStreamDataType.text,
            web_searcher: [],
          },
        ],
        current_msg: null,
        cached_msg: null,
        session_id: "",
        status: EChatStatus.work,
      });

      // this.getGuessList(this.chat);
    }
  }

  public async selectSession(id: string, tempId?: string) {
    this.setFileSelectText(null);
    this.setCurrentWebSearch([]);
    this.sessionChanging = true;
    this.setShowFileSelect(null);
    this.guess.stopGuessList();
    this.guess = new GuessModel();
    this.cachSession();
    const cachSession = this.findCachSession(id, tempId);
    const isVideo =
      this.chatModel === EChatModel.Video ||
      this.chatModel === EChatModel.VideoEdit ||
      this.chatModel === EChatModel.VideoR1;
    if (cachSession) {
      this.session = cachSession;
      if (isVideo) {
        if (!this.session.firstMessage) {
          this.session.setFirstMessage(this.session.chatInstanceList[0]);
        }
        // 初始化视频
        this.initVideoFile();
        this.setChatModel(EChatModel.VideoEdit);
      }
    } else {
      this.session = new Session(id);
      this.chatSampleManager = null;
      this.chat = null;
      this.setPreviewFile(null);

      VM.saveSessionInfo2Storage(id, isVideo ? "video" : undefined);
      if (isVideo) {
        await this.session.getFirstMessage();
        // 初始化视频
        this.initVideoFile();
        this.setChatModel(EChatModel.VideoEdit);
      }
      await this.session.getSessionFullInfo(
        this.chatModel === EChatModel.Video ||
          this.chatModel === EChatModel.VideoEdit ||
          this.chatModel === EChatModel.VideoR1,
      );
    }
    // await this.stopStreamingChat();

    // 获取猜你想问
    if (this.session.chatInstanceList && this.session.chatInstanceList.length) {
      this.getGuessList(
        this.session.chatInstanceList[this.session.chatInstanceList.length - 1],
      );
    }
    this.sessionChanging = false;
  }

  @flow
  public *newSession() {
    this.setFileSelectText(null);
    this.setCurrentWebSearch([]);
    this.video.init();
    if (
      this.chatModel === EChatModel.VideoEdit ||
      this.chatModel === EChatModel.VideoR1
    ) {
      this.chatModel = EChatModel.Video;
    }
    this.setShowFileSelect(null);
    this.guess.stopGuessList();
    this.cachSession();
    // yield this.stopStreamingChat();
    this.session = new Session("");
    this.setPreviewFile(null);
    yield this.resetChatSample();
    VM.cleanCachedSessionInfo();
  }

  @action.bound
  public selectSample(sample: IChatSample) {
    const msg = [
      {
        msg_type: ESendMsgType.user_query,
        user_query: sample.query?.[0].message ?? "",
      },
    ];
    const file = sample.query?.[0].file.map(ele => ({
      file_name: ele.name,
      content_type: ele.content_type,
      file_id: ele.id,
      url: ele.url,
      status: ESessionFileStatus.success,
    }));

    const fileList: ISessionFile[] = this.getFormatedFiles(
      file,
      msg[0].user_query,
    );

    if (this.isLogin) {
      this.chatSampleManager = null;
      const chat = this.session?.addChat(msg, fileList);
      this.session.fileManager?.addFiles(fileList);
      this.getGuessList(chat);
    } else {
      this.chatSampleManager = null;
      this.chat = new Chat({
        guidance: sample.query?.[0].id,
        type: EChatType.stream,
        streamingStatus: EStreamDataStatus.init,
        send_msg: msg,
        files: fileList,
        recv_msg: null,
        current_msg: null,
        cached_msg: null,
        session_id: "",
        status: EChatStatus.work,
      });
    }
  }

  async getGuessList(chat: Chat) {
    if (chat.streamingStatus !== EStreamDataStatus.streamingEnd) {
      await when(() => chat.streamingStatus === EStreamDataStatus.streamingEnd);
    }
    const invalideSendMsg = chat.send_msg?.find(
      item => item.msg_type === ESendMsgType.file_query,
    );
    const current_msg = chat.recv_msg ? chat.recv_msg : chat.cached_msg;
    const invalideRecvMsg = current_msg?.find(
      item => item.type === EStreamDataType.text2image,
    );

    const empty = !current_msg || current_msg.length === 0;

    const haveFile = chat.files && chat.files.length;

    if (
      chat.complete !== false &&
      !chat.isSensitive &&
      !invalideSendMsg &&
      !invalideRecvMsg &&
      !haveFile &&
      !empty
    ) {
      const send_msg = chat.send_msg;
      if (send_msg && current_msg) {
        this.guess.getGuessList(send_msg, current_msg, chat.session_id);
      }
    }
  }

  // 上传视频
  uploadVideo = async (fileList: File[]) => {
    try {
      // 确保 video 实例存在
      if (!this.video) {
        this.video = new Video();
      }
      const res = await this.video.uploadFiles(fileList);
      console.log("[uploadVideo vm] res", res);

      return res;
      // todo 上传成功后，处理UI
    } catch (error) {
      console.error("[uploadVideo vm] error", error);

      return { status: "error", message: "上传视频失败" };
    }
  };

  // 上传视频URL
  uploadVideoUrl = async (url: string) => {
    try {
      // 确保 video 实例存在
      if (!this.video) {
        this.video = new Video();
      }
      const res = await this.video.uploadUrl(url);
      if (res.code === 0) {
        this.video.setFileList(
          res.message.map(item => ({
            url: item.url,
            fileId: item.file_id,
            uploadStatus: EVideoUploadStatus.UPLOADED,
            thumbnailUrl: "",
          })),
        );
        await this.startVideoChat();
      }
      return res.code;
      // todo 上传成功后，处理UI
    } catch (error) {
      console.error("[uploadVideo vm] error", error);
      throw new Error("uploadVideoUrlError");
    }
  };

  @action.bound
  async startVideoChat() {
    return await this.sendMsg(
      "",
      this.video.fileList.map(item => ({
        file_id: item.fileId,
        file_name: "video",
        url: item.url,
        status: "",
      })),
    );
  }

  @action.bound
  handleSampleAfterAsk() {
    if (this.chatSampleManager) {
      this.chatSampleManager = null;
      this.session = new Session("");
    }
  }

  @action
  async reAsk(chat: Chat) {
    this.guess.stopGuessList();
    await chat.reAsk();
    this.getGuessList(chat);
  }

  @action.bound
  public sendMsg(
    text: string,
    fileList: UploadedFile[],
    fileInfo?: ISelection,
    fileTextType?: EFileQueryType,
    guess?: boolean,
  ) {
    this.guess.stopGuessList();
    if (fileTextType === EFileQueryType.query) {
      this.setFileSelectText(null);
    }
    const files = this.getFormatedFiles(fileList, text);

    // 拼接默认话术
    if (!!fileList.length && !text.trim().length) {
      const imgCount = files.filter(
        ({ type }) => type === ESessionFileType.image,
      ).length;
      const fileCount = files.filter(
        ({ type }) => type === ESessionFileType.file,
      ).length;
      if (imgCount) {
        text = imgCount > 1 ? `描述${imgCount}张图片的内容` : "描述图片的内容";
      }
      if (fileCount) {
        text =
          fileCount > 1
            ? `总结${fileCount}个文档的主要内容`
            : "总结文档的主要内容";
      }

      if (this.chatModel === EChatModel.Video) {
        text = "";
      }
    }

    const msg: IChatSendMessage[] = !fileInfo
      ? [
          {
            msg_type: ESendMsgType.user_query,
            user_query: text,
          },
        ]
      : [
          {
            msg_type: ESendMsgType.user_query,
            user_query: text,
          },
          {
            msg_type: ESendMsgType.file_query,
            file_query: {
              file_id: this.previewingFile!.file_id || "",
              file_query_msg: fileInfo!.text,
              file_query_type: fileTextType!,
              location: JSON.stringify({
                id: fileInfo.id,
                startPage: fileInfo.startPage,
                startId: fileInfo.startId,
                startOffset: fileInfo.startOffset,
                endPage: fileInfo.endPage,
                endId: fileInfo.endId,
                endOffset: fileInfo.endOffset,
              }),
            },
          },
        ];
    this.handleSampleAfterAsk();
    this.session.fileManager?.addFiles(files);
    const chat = this.session?.addChat(
      msg,
      files,
      guess,
      this.chatModel,
      this.web_search,
    );
    if (this.session.chatInstanceList.length === 1) {
      const firstChat = this.session.chatInstanceList[0];
      // this.handleFirstChat(firstChat.initialSessionTitle);
      // this.session.time = new Date().getTime();
      this.sessionManager?.addListSession(
        // @ts-ignore
        this.session,
        true,
      );
    }

    if (this.chatModel === EChatModel.Video) {
      this.chatModel = EChatModel.VideoEdit;
    }
    if (this.chatModel !== EChatModel.VideoEdit) {
      this.getGuessList(chat);
    }
  }

  @action.bound
  sendTransMsg(text: string) {
    this.guess.stopGuessList();
    const msg = [
      {
        msg_type: ESendMsgType.trans_query,
        trans_query: `翻译为${this.chatTargetLang.title}`,
      },
      {
        msg_type: ESendMsgType.user_query,
        user_query: `"${text}"`,
      },
    ];
    this.handleSampleAfterAsk();
    const chat = this.session?.addChat(msg);
    this.getGuessList(chat);
  }

  @action.bound
  handleFirstChat(title: string) {
    if (!this.session.session_id) return;
    const cachedSession = VM.getCachedSessionInfo(
      this.chatModel === EChatModel.Video ||
        this.chatModel === EChatModel.VideoEdit
        ? "video"
        : undefined,
    );
    if (!cachedSession || cachedSession.sesion_id !== this.session.session_id) {
      this.syncSessionToListAndStorage(title);
    }
  }

  @action.bound
  public syncSessionToListAndStorage(title: string) {
    VM.saveSessionInfo2Storage(
      this.session.session_id,
      this.chatModel === EChatModel.Video ||
        this.chatModel === EChatModel.VideoEdit ||
        this.chatModel === EChatModel.VideoR1
        ? "video"
        : undefined,
    );
    this.sessionManager?.addListSession(
      {
        session_id: this.session.session_id,
        status: this.session.status,
        time: moment().format("YYYY-MM-DD HH:mm:ss"),
        title,
      },
      true,
    );
  }

  @flow.bound
  *stopStreamingChat() {
    const streamingChat = this.session.chatInstanceList.find(
      item =>
        item.streamingStatus === EStreamDataStatus.streaming ||
        item.streamingStatus === EStreamDataStatus.init,
    );
    if (streamingChat) {
      yield streamingChat.stop();
    }
  }

  @flow.bound
  public *resetChatSample() {
    this.chatSampleManager = new ChatSampleManager();
    yield this.chatSampleManager.listMember();
  }

  @flow.bound
  public *resetHome() {
    yield this.resetChatSample();
    this.chat = null;
  }

  @flow
  *editSessionName(name: string, id?: string) {
    const sessionId = id || this.session.session_id;
    if (!sessionId) return;

    if (sessionId === this.session.session_id) {
      yield this.session.editName(name);
      // yield this.sessionManager?.listMember();
    } else {
      yield this.sessionManager?.editNameById(sessionId, name);
    }

    this.sessionManager?.updateSessionTitle(sessionId, name);
  }

  @flow.bound
  *deleteFileFromDir(file_id: string) {
    yield this.session.deleteSessionFile(file_id);
  }

  private getFormatedFiles(
    file_list: UploadedFile[],
    text: string,
  ): ISessionFile[] {
    const fileList: ISessionFile[] = file_list.map(
      item =>
        new SessionFile({
          type: isImageFileName(item.file_name)
            ? ESessionFileType.image
            : ESessionFileType.file,
          file_id: item.file_id || "",
          file_name: item.file_name,
          url: item.url,
          status: ESessionFileStatus.success,
          contentParseStatus: ESessionFileContentParseStatus.success,
        }),
    );

    const urlFileList: ISessionFile[] = SessionFile.instanceFileByText(text);
    return [...fileList, ...urlFileList];
  }

  @flow.bound
  *deleteSession(id: string) {
    yield this.sessionManager?.delete(id);
    if (id === this.session.session_id) {
      yield this.newSession();
    }
  }

  public selectFileText = (type: EFileQueryType, v: ISelection) => {
    if (type === EFileQueryType.query) {
      this.setFileSelectText(v);
    } else {
      this.sendMsg(FILE_QUERY_MSG[type], [], v, type);
    }
  };

  public removeFileText = () => {
    this.setFileSelectText(null);
  };

  public highLightSelectText = (v: ISelection, fileId: string) => {
    if (this.previewingFile?.file_id === fileId) {
      this.setShowFileSelect(v);
    } else {
      this.setPreviewFile(
        this.session.fileManager?.getFileById(fileId) || null,
      );
      this.setShowFileSelect(v);
    }
  };

  public deleteKeyPoint = (v: ISelection & { msgId?: string }) => {
    if (v.msgId) {
      this.session.deleteChat(v.msgId);
    }
  };

  @flow.bound
  *resetInit() {
    yield this.newSession();
    this.sessionManager!.list = [];
    // 不重置视频状态
  }

  static SESSION_SAVE_TAG = "session";

  // 清除localstorage中缓存的session信息
  static async cleanCachedSessionInfo() {
    await localStorage.removeItem("session");
    await localStorage.removeItem("video-session");
  }

  // 获取localstorage中缓存的session信息
  static getCachedSessionInfo(type?: string) {
    const info = localStorage.getItem(
      type === "video" ? "video-session" : "session",
    );
    if (!info) return null;
    try {
      return JSON.parse(info);
    } catch (error) {
      return null;
    }
  }

  // 存储session信息到localstorage中
  static saveSessionInfo2Storage(sessionId: string, type?: string) {
    localStorage.setItem(
      type === "video" ? "video-session" : "session",
      JSON.stringify({
        session_id: sessionId,
      }),
    );
  }

  // 缓存翻译语言
  static cacheTranslationLang(
    langs: { contentLang: IChatTransLang; targetLang: IChatTransLang } | null,
  ) {
    langs
      ? localStorage.setItem(LANG_CACHE_KEY, JSON.stringify(langs))
      : localStorage.removeItem(LANG_CACHE_KEY);
  }

  // 获取缓存翻译语言
  static getDefaultTranslationLang(): {
    contentLang: IChatTransLang;
    targetLang: IChatTransLang;
  } {
    const defualt = {
      contentLang: DEFAULT_CONTENT_LANG,
      targetLang: DEFAULT_TARGET_LANG,
    };
    const cachedLang = localStorage.getItem(LANG_CACHE_KEY);
    try {
      return cachedLang ? JSON.parse(cachedLang) : defualt;
    } catch (error) {
      return defualt;
    }
  }

  initVideoFile() {
    const files = this.session.firstMessage?.files;
    const firstSendMsgFile = files?.[0];
    if (firstSendMsgFile) {
      this.video.setFileList(
        files?.map(item => ({
          url: item.url,
          fileId: item.file_id || "",
          uploadStatus: EVideoUploadStatus.UPLOADED,
        })) || [],
      );
      // this.video.setFileList(firstSendMsgFile.url);
      // if (firstSendMsgFile.file_id) {
      //   this.video.setVideoFileId(firstSendMsgFile.file_id);
      // }
    }
  }

  @action.bound
  handleVideoTimeClick(v: { videoIndex: number; seekTime: string }) {
    this.video.currentVideo = {
      index: v.videoIndex === 0 ? this.video.currentVideo.index : v.videoIndex,
      seekTime: v.seekTime,
    };
  }

  @action.bound
  async handleStreamEnd(sessionId: string, chat: Chat) {
    if (this.session.session_id === sessionId && !this.session.name) {
      const res = await this.session.editNameByChat(chat);
      if (res) {
        this.sessionManager?.updateSessionTitle(sessionId, res);
      }
    }
  }

  cachSession() {
    if (this.session.session_id) {
      this.sessionMap[this.session.session_id] = this.session;
    } else if (this.session?.tempId) {
      this.sessionMap[this.session.tempId] = this.session;
    }
  }

  findCachSession(sessionId: string, tempId?: string) {
    let result = this.sessionMap[sessionId];
    if (!result && tempId) {
      result = this.sessionMap[tempId];
    }
    return result;
  }

  @action.bound
  showWebSearch(v: IWebSearch[]) {
    if (this.previewingFile) {
      this.setPreviewFile(null);
    }
    this.setCurrentWebSearch(v);
  }
}

export default VM;
